import React, { useState, useEffect } from 'react';
import './BulkProgressModal.css';

function BulkProgressModal({
  isOpen,
  onClose,
  projectId
}) {
  const [progressData, setProgressData] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (!isOpen || !projectId) return;

    const progressKey = `bulkPrepare_${projectId}`;

    // Poll localStorage for progress updates every 500ms
    const interval = setInterval(() => {
      const stored = localStorage.getItem(progressKey);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          setProgressData(data);

          if (data.status === 'completed') {
            setIsCompleted(true);
            clearInterval(interval);
          }
        } catch (e) {
          console.error('Error parsing progress data:', e);
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isOpen, projectId]);

  const getLanguageDisplayName = (langCode) => {
    const languageNames = {
      de: 'German',
      ar: 'Arabic',
      hi: 'Hindi',
      pt: 'Portuguese',
      ru: 'Russian',
      it: 'Italian',
      ta: 'Tamil',
      te: 'Telugu'
    };
    return languageNames[langCode] || langCode;
  };

  const getStepDisplayName = (step) => {
    const stepNames = {
      starting: 'Initializing...',
      adding_language: 'Adding language to project',
      generating_scripts: 'Generating AI scripts',
      generating_voices: 'Generating voice overs',
      generating_configurations: 'Generating video configurations',
      polling: 'Checking completion status',
      completed: 'Completed'
    };
    return stepNames[step] || step;
  };

  const handleClose = () => {
    if (isCompleted || !progressData || progressData.status === 'completed') {
      // Clear progress data when closing after completion
      const progressKey = `bulkPrepare_${projectId}`;
      localStorage.removeItem(progressKey);
    }
    onClose();
  };

  if (!isOpen) return null;

  const progress = progressData || {};
  const totalLanguages = progress.languages?.length || 0;
  const currentIndex = progress.currentIndex || 0;
  const completedLanguages = progress.completedLanguages?.length || 0;
  const currentLanguage = progress.currentLanguage;
  const currentStep = progress.currentStep || 'starting';

  const overallProgress = totalLanguages > 0 ? (completedLanguages / totalLanguages) * 100 : 0;
  const isProcessing = progress.status === 'processing';
  const startTime = progress.startTime;
  const elapsedTime = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-progress-modal">
        <h2>🚀 Bulk Prepare Progress</h2>

        <div className="modal-body">
          {/* Overall Progress */}
          <div className="progress-section">
            <div className="progress-header">
              <h3>Overall Progress</h3>
              <span className="progress-percentage">{Math.round(overallProgress)}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
            <p className="progress-summary">
              {completedLanguages} of {totalLanguages} languages completed
            </p>
          </div>

          {/* Current Task */}
          {isProcessing && currentLanguage && (
            <div className="current-task">
              <h3>Current Task</h3>
              <div className="task-info">
                <div className="task-language">
                  <span className="language-flag">🌐</span>
                  <span className="language-name">{getLanguageDisplayName(currentLanguage)}</span>
                </div>
                <div className="task-step">
                  <span className="step-icon">⚙️</span>
                  <span className="step-name">{getStepDisplayName(currentStep)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Language List */}
          <div className="languages-list">
            <h3>Languages</h3>
            <div className="language-items">
              {progress.languages?.map((lang, index) => {
                const isCompleted = progress.completedLanguages?.includes(lang);
                const isCurrent = currentLanguage === lang && isProcessing;
                const isPending = index >= currentIndex && !isCompleted;

                return (
                  <div
                    key={lang}
                    className={`language-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isPending ? 'pending' : ''}`}
                  >
                    <span className="language-status-icon">
                      {isCompleted ? '✅' : isCurrent ? '🔄' : '⏳'}
                    </span>
                    <span className="language-name">{getLanguageDisplayName(lang)}</span>
                    {isCurrent && (
                      <span className="current-step">{getStepDisplayName(currentStep)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats */}
          <div className="stats-section">
            <div className="stat">
              <span className="stat-label">Elapsed Time</span>
              <span className="stat-value">{Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s</span>
            </div>
            <div className="stat">
              <span className="stat-label">Status</span>
              <span className={`stat-value status-${progress.status}`}>
                {isCompleted ? 'Completed' : 'Processing'}
              </span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            onClick={handleClose}
            className={`close-button ${isCompleted ? 'completed' : ''}`}
            disabled={isProcessing && !isCompleted}
          >
            {isCompleted ? 'Done' : isProcessing ? 'Processing...' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkProgressModal;